import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { AppointmentActionEnum, AppointmentStateEnum, useMeetingsUpdateAppointmentStatusMutation } from '@/gql/myomr'

import { useMeetingsBase } from './useMeetingsBase'

export { type AppointmentNode } from './useMeetingsBase'

export function useMeetingsRequests() {
  const { t } = useI18n()
  const base = useMeetingsBase([AppointmentStateEnum.Created])
  const actionLoadingId = ref<string | null>(null)
  const { mutate: updateAppointmentStatus } = useMeetingsUpdateAppointmentStatusMutation()

  async function acceptRequest(appointmentId: string): Promise<boolean> {
    actionLoadingId.value = appointmentId
    try {
      const result = await updateAppointmentStatus({
        calendarAppointmentId: appointmentId,
        action: AppointmentActionEnum.Approve,
      })

      if (result?.data?.meetingsUpdateAppointmentStatus?.errors?.length) {
        result.data.meetingsUpdateAppointmentStatus.errors.forEach((error) => {
          if (error) {
            // Check if it's a slot capacity error
            if (error.toLowerCase().includes('slot') || error.toLowerCase().includes('capacity')) {
              base.showToast(t('Meetings.requests.slotFullError'), 'error')
            } else {
              base.showToast(error, 'error')
            }
          }
        })
        return false
      }

      base.showToast(t('Meetings.requests.requestAccepted'), 'success')
      await base.refetch()
      base.refetchCounters()
      return true
    } catch (_error) {
      base.showToast(t('common.errorOccurred'), 'error')
      return false
    } finally {
      actionLoadingId.value = null
    }
  }

  async function declineRequest(appointmentId: string, denialReason?: string): Promise<boolean> {
    actionLoadingId.value = appointmentId
    try {
      const result = await updateAppointmentStatus({
        calendarAppointmentId: appointmentId,
        action: AppointmentActionEnum.Deny,
        denialReason,
      })

      if (result?.data?.meetingsUpdateAppointmentStatus?.errors?.length) {
        result.data.meetingsUpdateAppointmentStatus.errors.forEach((error) => {
          if (error) base.showToast(error, 'error')
        })
        return false
      }

      base.showToast(t('Meetings.requests.requestDeclined'), 'success')
      await base.refetch()
      base.refetchCounters()
      return true
    } catch (_error) {
      base.showToast(t('common.errorOccurred'), 'error')
      return false
    } finally {
      actionLoadingId.value = null
    }
  }

  return {
    ...base,
    actionLoadingId,
    acceptRequest,
    declineRequest,
  }
}
